/**
 * Offline Logo Hook
 * 
 * Provides cached logo URLs for offline-first functionality in authenticated app areas.
 * Falls back to original URLs when cache is unavailable.
 */

import { useState, useEffect } from 'react';
import { logoAssetManager } from '@/services/offline';

/**
 * Hook for accessing cached logos with offline support
 */
export function useOfflineLogo(originalUrl: string): {
  logoUrl: string;
  isFromCache: boolean;
  isLoading: boolean;
  error: string | null;
} {
  const [logoUrl, setLogoUrl] = useState<string>(originalUrl);
  const [isFromCache, setIsFromCache] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadCachedLogo = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Check if logo is cached
        const isCached = await logoAssetManager.isLogoCached(originalUrl);

        if (isCached) {
          // Get cached logo as data URL (better for Capacitor)
          const cachedUrl = await logoAssetManager.getCachedLogoUrl(originalUrl);


          if (cachedUrl && isMounted) {
            setLogoUrl(cachedUrl);
            setIsFromCache(true);

          } else {
            // Cache exists but failed to create data URL
            if (isMounted) {

              // In offline mode, don't fallback to original URL as it won't work
              if (navigator.onLine) {
                setLogoUrl(originalUrl);
                setIsFromCache(false);

              } else {
                setError('Cached logo unavailable in offline mode');
                setLogoUrl(originalUrl); // Try original URL anyway
                setIsFromCache(false);

              }
            }
          }
        } else {
          // No cache available
          if (isMounted) {

            if (navigator.onLine) {
              setLogoUrl(originalUrl);
              setIsFromCache(false);

            } else {
              // When offline and no cache, try the original URL anyway
              // The browser might have it cached, or we'll get a fallback
              setError('Logo not cached and offline');
              setLogoUrl(originalUrl); // Try original URL even when offline
              setIsFromCache(false);

            }
          }
        }
      } catch (err) {
        if (isMounted) {
          const errorMessage = err instanceof Error ? err.message : 'Unknown error';
          setError(errorMessage);


          // Try original URL even when offline as a last resort
          setLogoUrl(originalUrl);

          setIsFromCache(false);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);

        }
      }
    };

    loadCachedLogo();

    // Cleanup function - no need to revoke data URLs
    return () => {
      isMounted = false;
    };
  }, [originalUrl]);

  return {
    logoUrl,
    isFromCache,
    isLoading,
    error
  };
}

/**
 * Hook specifically for sidebar logo
 */
export function useSidebarLogo() {
  return useOfflineLogo('/lovable-uploads/309c4bff-08a2-44a3-9eb8-307bc935b2a8.png');
}

/**
 * Hook specifically for mobile header logo
 */
export function useMobileHeaderLogo() {
  return useOfflineLogo('/lovable-uploads/b9e6fa8f-f749-4515-b67c-d28715d804c5.png');
}

/**
 * Hook specifically for user avatar
 */
export function useUserAvatar() {
  return useOfflineLogo('/lovable-uploads/aa1f7238-2168-4dae-8ddc-d3d93b7cc03d.png');
}

/**
 * Hook for preloading logos (useful for warming cache)
 */
export function useLogoPreloader(): {
  preloadLogos: () => Promise<void>;
  isPreloading: boolean;
  preloadError: string | null;
} {
  const [isPreloading, setIsPreloading] = useState<boolean>(false);
  const [preloadError, setPreloadError] = useState<string | null>(null);

  const preloadLogos = async () => {
    try {
      setIsPreloading(true);
      setPreloadError(null);


      const result = await logoAssetManager.cacheAllLogos();

      if (!result.success) {
        throw new Error(`Failed to preload some logos: ${result.errors.join(', ')}`);
      }


    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setPreloadError(errorMessage);
      console.error('❌ Logo preloading failed:', err);
    } finally {
      setIsPreloading(false);
    }
  };

  return {
    preloadLogos,
    isPreloading,
    preloadError
  };
}
